import { useState, useCallback } from 'react';
import axios from 'axios';
import { enhancedApi } from '../../../shared/services/enhancedApi';
import type { ForexConversionRequest, ForexConversionResponse } from '../types/forex-conversion';

interface UseForexRateConversionReturn {
  convertCurrency: (request: ForexConversionRequest) => Promise<ForexConversionResponse>;
  loading: boolean;
  error: string | null;
  clearError: () => void;
}

export const useForexRateConversion = (): UseForexRateConversionReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const convertCurrency = useCallback(async (
    request: ForexConversionRequest
  ): Promise<ForexConversionResponse> => {
    setLoading(true);
    setError(null);

    try {
      console.log('useForexRateConversion: Converting currency with request:', request);

      // Validate request parameters
      if (!request.currentCurrency || !request.targetCurrency || !request.amount) {
        throw new Error('Invalid conversion request parameters');
      }

      if (request.amount <= 0) {
        throw new Error('Amount must be greater than zero');
      }

      if (request.currentCurrency === request.targetCurrency) {
        throw new Error('Current and target currencies cannot be the same');
      }

      // Make API call using enhancedApi for rate limiting and retry logic
      const response = await enhancedApi.post<ForexConversionResponse>(
        '/api/fxrates/convert',
        {
          currentCurrency: request.currentCurrency,
          targetCurrency: request.targetCurrency,
          amount: request.amount
        },
        {
          timeout: 30000, // 30 second timeout
          headers: {
            'Content-Type': 'application/json',
          }
        },
        2 // High priority for forex conversion
      );

      console.log('useForexRateConversion: Conversion successful:', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || 'Currency conversion failed');
      }

    } catch (err) {
      console.error('useForexRateConversion: Conversion failed:', err);
      
      let errorMessage = 'Failed to convert currency';
      
      if (axios.isAxiosError(err)) {
        if (err.response) {
          // Server responded with error status
          const serverMessage = err.response.data?.message || err.response.statusText;
          errorMessage = `Server error (${err.response.status}): ${serverMessage}`;
        } else if (err.request) {
          // Request was made but no response received
          errorMessage = 'Network error: Unable to connect to forex service. Please check your internet connection.';
        } else {
          // Something else happened
          errorMessage = `Request error: ${err.message}`;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Return error response in the expected format
      return {
        success: false,
        code: axios.isAxiosError(err) && err.response?.status ? err.response.status : 500,
        convertedAmount: 0,
        convertedCurrency: request.targetCurrency,
        currencyId: 0,
        message: errorMessage
      };

    } finally {
      setLoading(false);
    }
  }, []);

  return {
    convertCurrency,
    loading,
    error,
    clearError
  };
};